// Scroll Heading Handler
// - Zerlegt .heading h2 in einzelne Wörter
// - Animiert Wörter einzeln beim Scrollen (blur + skew + opacity)
// - Start: wenn h2 sichtbar wird (unten im Viewport)
// - Ende: wenn h2 oberhalb der Bildschirmmitte

interface WordSpan {
	span: HTMLSpanElement;
}

interface HeadingConfig {
	heading: HTMLElement;
	h2: HTMLElement;
	wordSpans: WordSpan[];
}

export function initScrollHeading(): void {
	const headings = document.querySelectorAll('.heading h2[data-animate]');
	
	if (!headings.length) return;

	const configs: HeadingConfig[] = Array.from(headings)
		.map(h2 => {
			const heading = h2.closest('.heading');
			if (!heading) return null;

			// Original-Kindknoten merken, dann h2 leeren.
			// Wir laufen über die Knoten (statt textContent), damit Inline-Markup
			// wie <sup class="reg">®</sup> erhalten bleibt.
			const originalNodes = Array.from(h2.childNodes);
			h2.innerHTML = '';

			const wordSpans: WordSpan[] = [];
			let current: HTMLSpanElement | null = null;

			const startWord = (): HTMLSpanElement => {
				const span = document.createElement('span');
				span.style.display = 'inline-block';
				span.style.marginRight = '0.3em';
				span.style.willChange = 'filter, transform, opacity';
				h2.appendChild(span);
				wordSpans.push({ span });
				current = span;
				return span;
			};

			originalNodes.forEach(node => {
				if (node.nodeType === Node.TEXT_NODE) {
					// Text an Whitespace zerlegen; Leerraum trennt Wörter
					(node.textContent || '').split(/(\s+)/).forEach(part => {
						if (part === '') return;
						if (/^\s+$/.test(part)) {
							current = null; // Worttrenner
						} else {
							(current ?? startWord()).appendChild(document.createTextNode(part));
						}
					});
				} else {
					// Inline-Element (z. B. <sup>) an aktuelles Wort hängen
					(current ?? startWord()).appendChild(node.cloneNode(true));
				}
			});

			return {
				heading: heading as HTMLElement,
				h2: h2 as HTMLElement,
				wordSpans,
			};
		})
		.filter((config): config is HeadingConfig => config !== null);

	if (!configs.length) return;

	// Update Heading-Animation kontinuierlich beim Scroll
	const updateHeadingAnimation = () => {
		configs.forEach(({ heading, wordSpans }) => {
			// Bounding Rect vom Heading
			const rect = heading.getBoundingClientRect();
			const headingCenter = rect.top + rect.height / 2;

			// Viewpoint-Breiten
			const viewportHeight = window.innerHeight;

			// Start: Element kommt unten in Sicht (viewportHeight)
			// Ende: Element ist oberhalb der Bildschirmmitte (viewportHeight / 2)
			const startPoint = viewportHeight; // Unten - Animation beginnt
			const endPoint = viewportHeight / 2; // Mitte - Animation abgeschlossen

			// Position des Heading-Mittelpunkts bei MAXIMALEM Scroll.
			// (Konstant pro Heading, da rect.top und remainingScroll sich beim
			//  Scrollen gegenseitig aufheben.) Headings direkt vor dem Footer
			//  können den endPoint sonst nie erreichen und blieben unscharf.
			const maxScrollY = document.documentElement.scrollHeight - viewportHeight;
			const remainingScroll = Math.max(0, maxScrollY - window.scrollY);
			const reachableCenter = headingCenter - remainingScroll;

			// Erreichbaren Zielpunkt nehmen, falls der normale endPoint zu hoch
			// liegt. Für erreichbare Headings bleibt es exakt beim endPoint.
			const effectiveEndPoint = Math.max(endPoint, reachableCenter);
			const activationRange = startPoint - effectiveEndPoint;

			// Fortschritt je näher Element nach oben kommt, clamped zwischen 0 und 1
			const distanceFromMidpoint = startPoint - headingCenter;
			let overallProgress = activationRange > 0 ? distanceFromMidpoint / activationRange : 1;
			overallProgress = Math.max(0, Math.min(1, overallProgress));

			// Berechne gesamte Stagger-Verzögerung (vom ersten bis letzten Wort)
			const totalWordDelay = (wordSpans.length - 1) * 0.08;
			
			// Skaliere overallProgress so, dass die Animation über ALLE Wörter läuft
			const adjustedProgress = overallProgress * (1 + totalWordDelay);

			// Für jedes Wort: individueller Fortschritt
			wordSpans.forEach((word, index) => {
				// Jedes Wort animiert mit Stagger-Effekt
				const wordDelay = index * 0.08; // 8% Stagger pro Wort
				
				// Fortschritt für dieses Wort (0 bis 1, mit Stagger)
				const wordProgress = Math.max(0, Math.min(1, adjustedProgress - wordDelay));

				// Interpoliere Blur-Effekt
				const blur = 8 * (1 - wordProgress); // Von 8px zu 0px
				const skew = -20 * (1 - wordProgress); // Von -20deg zu 0deg
				const opacity = wordProgress; // Von 0 zu 1

				// CSS anwenden
				word.span.style.filter = `blur(${blur}px)`;
				word.span.style.transform = `skew(${skew}deg)`;
				word.span.style.opacity = String(opacity);
			});
		});
	};

	// Mit requestAnimationFrame für optimale Performance
	const animationLoop = () => {
		updateHeadingAnimation();
		requestAnimationFrame(animationLoop);
	};

	animationLoop();
}
